import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api";
import { Heartbeat, Warning, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";

export const LedgerHealthCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api
      .get("/ledger-health")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;

  const { healthy, settlements_without_log: sl, sales_missing_price: sp } = data;

  return (
    <section
      className={`border rounded-xl p-4 sm:p-5 shadow-sm ${
        healthy ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50/50 border-amber-300"
      }`}
      data-testid="ledger-health-card"
    >
      <div className="flex items-center gap-2.5">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center border ${
            healthy
              ? "bg-emerald-100 border-emerald-200 text-emerald-700"
              : "bg-amber-100 border-amber-300 text-amber-700"
          }`}
        >
          {healthy ? <CheckCircle size={18} weight="bold" /> : <Warning size={18} weight="bold" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Heartbeat size={14} weight="bold" className="text-zinc-500 shrink-0" />
            <h2 className="text-sm font-semibold tracking-tight text-zinc-950">
              {t("ledger_health.title")}
            </h2>
          </div>
          <p
            className={`text-xs mt-0.5 ${healthy ? "text-emerald-800" : "text-amber-800"}`}
            data-testid={healthy ? "ledger-health-ok" : "ledger-health-issues"}
          >
            {healthy
              ? t("ledger_health.all_good")
              : t("ledger_health.issues_found", { n: sl.count + sp.count })}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="p-2 rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
          title={t("ledger_health.recheck")}
          data-testid="ledger-health-refresh"
        >
          <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      {!healthy && (
        <div className="mt-3 space-y-2">
          {sl.count > 0 && (
            <div className="text-xs text-zinc-700" data-testid="ledger-health-unlogged">
              <Link to="/settlements" className="font-semibold text-amber-900 underline underline-offset-2">
                {t("ledger_health.unlogged_settlements", { n: sl.count })}
              </Link>
              <ul className="mt-1 space-y-0.5">
                {sl.items.map((s) => (
                  <li key={s.id} className="text-zinc-600">
                    • {s.received_from || "—"} — ${Number(s.gross_amount || 0).toFixed(2)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {sp.count > 0 && (
            <div className="text-xs text-zinc-700" data-testid="ledger-health-unpriced">
              <Link to="/movement-log" className="font-semibold text-amber-900 underline underline-offset-2">
                {t("ledger_health.unpriced_sales", { n: sp.count })}
              </Link>
              <ul className="mt-1 space-y-0.5">
                {sp.items.map((r) => (
                  <li key={r.id} className="text-zinc-600">
                    • {r.quantity} × {r.product_name} → {r.to_user_name || r.recipient_name || "—"}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[11px] text-amber-700 italic">{t("ledger_health.deploy_hint")}</p>
        </div>
      )}
    </section>
  );
};
